import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactModal from './ContactModal';
import { getSchoolConfig } from '../utils/domainConfig';
import { FaGraduationCap, FaShieldAlt, FaBus, FaChalkboardTeacher, FaFutbol, FaPuzzlePiece, FaHandsHelping, FaTint, FaStar, FaQuoteLeft, FaPhone, FaEnvelope, FaMapMarkerAlt, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import '../styles/HomePage.css';

const FACILITIES = [
  { icon: <FaShieldAlt />, title: 'Fully CCTV Secured', desc: 'Complete campus surveillance for maximum security and safety of students.', color: '#e74c3c' },
  { icon: <FaTint />, title: 'Fresh Drinking Water', desc: 'Purified water stations throughout the campus for hydration and health.', color: '#3498db' },
  { icon: <FaBus />, title: 'Transportation', desc: 'Safe and comfortable transportation service from various locations.', color: '#f39c12' },
  { icon: <FaChalkboardTeacher />, title: 'Best Teachers', desc: 'Highly qualified and experienced teaching staff dedicated to success.', color: '#2ecc71' },
  { icon: <FaFutbol />, title: 'Big Playing Ground', desc: 'Large sports field for physical activities and sports competitions.', color: '#9b59b6' },
  { icon: <FaPuzzlePiece />, title: 'Kids Play Zone', desc: 'Educational toys and play equipment for early childhood development.', color: '#e67e22' },
  { icon: <FaHandsHelping />, title: 'Clean Bathrooms', desc: 'Regularly sanitized and well-maintained bathroom facilities.', color: '#1abc9c' },
  { icon: <FaGraduationCap />, title: 'Quality Education', desc: 'Comprehensive curriculum with focus on holistic student development.', color: '#34495e' },
];

const STATS = [
  { number: '400+', label: 'Students' },
  { number: '20+', label: 'Teachers' },
  { number: '15+', label: 'Classes' },
  { number: '3+', label: 'Years' },
];

const TESTIMONIALS = [
  { text: 'The teachers at our school are exceptional. My child has shown remarkable progress in both academics and extracurricular activities.', author: 'Priya Sharma', role: 'Parent' },
  { text: "I'm impressed with the safety measures. The CCTV coverage and caring staff give me peace of mind when my children are at school.", author: 'Rajesh Kumar', role: 'Parent' },
  { text: 'The transportation facility is very reliable. My children reach school safely every day. Very satisfied with the service.', author: 'Sunita Devi', role: 'Parent' },
];

function HomePage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});
  const navigate = useNavigate();
  const schoolConfig = getSchoolConfig();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const setRef = (id) => (el) => { sectionRefs.current[id] = el; };

  return (
    <div className="hp-container">
      {/* Hero Section */}
      <section className="hp-hero">
        <div className="hp-hero-bg">
          <div className="hp-hero-overlay" />
        </div>
        <div className="hp-hero-content">
          <div className="hp-hero-badge">Excellence in Education Since {schoolConfig.establishedYear}</div>
          <h1>{schoolConfig.fullName.split(' ').slice(0, 2).join(' ')}<br /><span>{schoolConfig.fullName.split(' ').slice(2).join(' ')}</span></h1>
          <p className="hp-hero-tagline">{schoolConfig.schoolName} - Where Education Meets Excellence</p>
          <p className="hp-hero-sub">Nurturing young minds with quality education, modern facilities, and values that last a lifetime.</p>
          <div className="hp-hero-actions">
            <button className="hp-btn-primary" onClick={() => navigate('/login')}>
              Admin Login <FaArrowRight />
            </button>
            <button className="hp-btn-outline" onClick={() => setIsContactModalOpen(true)}>
              <FaPhone /> Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="hp-stats" id="stats" ref={setRef('stats')}>
        {STATS.map((stat, i) => (
          <div key={i} className={`hp-stat-item ${visibleSections.has('stats') ? 'visible' : ''}`} style={{ animationDelay: `${i * 0.15}s` }}>
            <span className="hp-stat-number">{stat.number}</span>
            <span className="hp-stat-label">{stat.label}</span>
          </div>
        ))}
      </section>

      {/* About Section */}
      <section className="hp-about" id="about" ref={setRef('about')}>
        <div className={`hp-about-inner ${visibleSections.has('about') ? 'visible' : ''}`}>
          <div className="hp-about-text">
            <h2 className="hp-section-title">About Our School</h2>
            <p>{schoolConfig.schoolName} School is committed to providing quality education with modern facilities in a nurturing environment. Our mission is to develop young minds into responsible citizens with strong values and academic excellence.</p>
            <div className="hp-about-highlights">
              <div className="hp-highlight"><FaCheckCircle /> CBSE Affiliated Curriculum</div>
              <div className="hp-highlight"><FaCheckCircle /> Holistic Development Focus</div>
              <div className="hp-highlight"><FaCheckCircle /> Safe & Secure Campus</div>
              <div className="hp-highlight"><FaCheckCircle /> Modern Teaching Methods</div>
            </div>
          </div>
          <div className="hp-about-image">
            <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Students learning" />
            <div className="hp-about-image-accent" />
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="hp-facilities" id="facilities" ref={setRef('facilities')}>
        <h2 className="hp-section-title">Our Facilities</h2>
        <p className="hp-section-subtitle">State-of-the-art facilities to ensure the best learning environment</p>
        <div className="hp-facilities-grid">
          {FACILITIES.map((f, i) => (
            <div
              key={i}
              className={`hp-facility-card ${visibleSections.has('facilities') ? 'visible' : ''}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="hp-facility-icon" style={{ background: `${f.color}15`, color: f.color }}>
                {f.icon}
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="hp-testimonials" id="testimonials" ref={setRef('testimonials')}>
        <h2 className="hp-section-title">What Parents Say</h2>
        <p className="hp-section-subtitle">Trusted by hundreds of families in Madhubani</p>
        <div className="hp-testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className={`hp-testimonial-card ${visibleSections.has('testimonials') ? 'visible' : ''}`}
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              <FaQuoteLeft className="hp-quote-icon" />
              <p>{t.text}</p>
              <div className="hp-testimonial-footer">
                <div className="hp-testimonial-avatar">{t.author.charAt(0)}</div>
                <div>
                  <strong>{t.author}</strong>
                  <span>{t.role}</span>
                </div>
                <div className="hp-testimonial-stars">
                  {[...Array(5)].map((_, j) => <FaStar key={j} />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact / CTA Section */}
      <section className="hp-cta" id="cta" ref={setRef('cta')}>
        <div className={`hp-cta-inner ${visibleSections.has('cta') ? 'visible' : ''}`}>
          <h2>Ready to Join {schoolConfig.schoolName}?</h2>
          <p>Give your child the education they deserve. Contact us for admissions and more information.</p>
          <div className="hp-cta-contacts">
            {schoolConfig.phones.map(phone => (
              <a key={phone} href={`tel:+91${phone}`} className="hp-cta-contact">
                <FaPhone /> +91 {phone}
              </a>
            ))}
            <a href={`mailto:${schoolConfig.email}`} className="hp-cta-contact">
              <FaEnvelope /> {schoolConfig.email}
            </a>
          </div>
          <div className="hp-cta-address">
            <FaMapMarkerAlt /> Madhubani, Bihar, India
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="hp-footer">
        <p>&copy; 2026 {schoolConfig.fullName} ({schoolConfig.schoolName}). All Rights Reserved.</p>
      </footer>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </div>
  );
}

export default HomePage;
